import React from 'react'
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	ChipPropsVariantOverrides,
	Box,
	Typography,
} from '@mui/material'
import { WorkItem } from '../../types/Work/WorkItem'

interface WorkItemReadonlyTableProps {
	workItems: WorkItem[]
}

interface HierarchicalWorkItem extends WorkItem {
	level: number
	children: HierarchicalWorkItem[]
}

export default function WorkItemReadonlyTable({ workItems }: WorkItemReadonlyTableProps) {
	const getLayerVariant = (wbsIndex: number): keyof ChipPropsVariantOverrides => {
		const layerIndex = Math.min(Math.max(wbsIndex, 1), 5)
		return `Layer${layerIndex}WorkItem` as keyof ChipPropsVariantOverrides
	}

	// Build hierarchy from flat workItems array
	const buildHierarchy = (flatItems: WorkItem[]): HierarchicalWorkItem[] => {
		const itemMap = new Map<number, HierarchicalWorkItem>()
		const rootItems: HierarchicalWorkItem[] = []

		// First pass: create all items with empty children arrays
		flatItems.forEach((item) => {
			itemMap.set(item.id, {
				...item,
				level: 0,
				children: [],
			})
		})

		// Second pass: build parent-child relationships
		flatItems.forEach((item) => {
			const hierarchicalItem = itemMap.get(item.id)!

			if (item.parentId && itemMap.has(item.parentId)) {
				const parent = itemMap.get(item.parentId)!
				hierarchicalItem.level = parent.level + 1
				parent.children.push(hierarchicalItem)
			} else {
				// Root item
				hierarchicalItem.level = 0
				rootItems.push(hierarchicalItem)
			}
		})

		return rootItems
	}

	// Flatten hierarchy in depth-first order for display
	const flattenDepthFirst = (hierarchicalItems: HierarchicalWorkItem[]): HierarchicalWorkItem[] => {
		const result: HierarchicalWorkItem[] = []

		const traverse = (items: HierarchicalWorkItem[]) => {
			// Sort by wbsIndex to maintain proper order within each level
			const sortedItems = [...items].sort((a, b) => a.wbsIndex - b.wbsIndex)

			sortedItems.forEach((item) => {
				result.push(item)
				if (item.children.length > 0) {
					traverse(item.children)
				}
			})
		}

		traverse(hierarchicalItems)
		return result
	}

	const hierarchicalItems = buildHierarchy(workItems)
	const displayItems = flattenDepthFirst(hierarchicalItems)

	if (workItems.length === 0) {
		return (
			<Box sx={{ p: 2, textAlign: 'center' }}>
				<Typography variant='body2' color='text.secondary'>
					No work items to display
				</Typography>
			</Box>
		)
	}

	return (
		<TableContainer component={Paper} sx={{ maxHeight: 400 }}>
			<Table stickyHeader size='small'>
				<TableHead>
					<TableRow>
						<TableCell align='left'>Type</TableCell>
						<TableCell align='center'>WBS</TableCell>
						<TableCell sx={{ minWidth: 200 }}>Name</TableCell>
						<TableCell align='center'>Release Date</TableCell>
						<TableCell align='center'>NOC Date</TableCell>
						<TableCell align='right'>Qty Budget</TableCell>
						<TableCell align='center'>Unit</TableCell>
						<TableCell align='center'>Budget Hours</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{displayItems.map((item) => (
						<React.Fragment key={item.id}>
							{/* Work Item Row */}
							<TableRow>
								<TableCell align='left'>
									<Box sx={{ pl: item.level * 2 }}>
										<Chip label={item.typeName} variant={getLayerVariant(item.wbsIndex)} />
									</Box>
								</TableCell>
								<TableCell align='center'>{item.wbsName}</TableCell>
								<TableCell sx={{ minWidth: 200 }}>
									<Box>
										<Typography variant='body2'>{item.name}</Typography>
									</Box>
								</TableCell>
								<TableCell align='center'>
									<Typography variant='body2'>
										{new Date(item.releaseDate).toLocaleDateString()}
									</Typography>
								</TableCell>
								<TableCell align='center'>
									<Typography variant='body2'>
										{new Date(item.nocDate).toLocaleDateString()}
									</Typography>
								</TableCell>
								<TableCell align='right'>
									<Typography variant='body2'>{item.quantityBudgeted.toLocaleString()}</Typography>
								</TableCell>
								<TableCell align='center'>
									<Typography variant='body2'>{item.unitOfMeasure}</Typography>
								</TableCell>
								<TableCell align='center'>
									<Typography variant='body2'>{item.budgetedHours.toLocaleString()}</Typography>
								</TableCell>
							</TableRow>
							
							{/* Task Rows */}
							{item.tasks && item.tasks.length > 0 && item.tasks.map((task) => (
								<TableRow key={`task-${task.id}`} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
									<TableCell align='left'>
										<Box sx={{ pl: (item.level + 1) * 2 }}>
											<Chip 
												label="Task" 
												variant={'task'}
												 
											/>
										</Box>
									</TableCell>
									<TableCell align='center'>-</TableCell>
									<TableCell sx={{ minWidth: 200 }}>
										<Box>
											<Typography variant='body2'>
												{task.name}
											</Typography>
										</Box>
									</TableCell>
									<TableCell align='center'>-</TableCell>
									<TableCell align='center'>-</TableCell>
									<TableCell align='right'>-</TableCell>
									<TableCell align='center'>-</TableCell>
									<TableCell align='center'>-</TableCell>
								</TableRow>
							))}
						</React.Fragment>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	)
}
